import { lazy, Suspense, useEffect, useRef } from 'react'
import { Route, Switch, Link, useLocation } from 'wouter'
import { Home as HomeIcon, User, ShieldCheck } from 'lucide-react'
import { trpc } from './lib/trpc'
import { useTelegram } from './hooks/useTelegram'
import { stashPromoFromUrl } from './lib/promoStash'
import { captureTrafficSource, trackHit } from './lib/metrika'
import ChatErrorBoundary from './components/ChatErrorBoundary'
import { isAdminIdentifier } from '@coinsrubot/shared'
import { HeaderV2, FooterV2, BottomNavV2 } from './v2/ShellV2'
import { useUiMode } from './v2/useUiMode'

const Home = lazy(() => import('./pages/Home'))
const HomeV2 = lazy(() => import('./v2/HomeV2'))
const ProductV2 = lazy(() => import('./v2/ProductPage'))
const GamesV2 = lazy(() => import('./v2/GamesV2'))
const Login = lazy(() => import('./pages/Login'))
const Profile = lazy(() => import('./pages/Profile'))
const PaymentBridge = lazy(() => import('./pages/PaymentBridge'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const PaymentFail = lazy(() => import('./pages/PaymentFail'))
const Chat = lazy(() => import('./pages/Chat'))
const SupportChat = lazy(() => import('./pages/SupportChat'))
const Admin = lazy(() => import('./pages/Admin'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const UserAgreement = lazy(() => import('./pages/UserAgreement'))
const Reviews = lazy(() => import('./pages/Reviews'))
const Games = lazy(() => import('./pages/Games'))

function Footer() {
  return (
    <footer
      style={{
        borderTop: '1px solid var(--color-line)',
        marginTop: 'auto',
        paddingTop: 28,
        paddingBottom: 'calc(max(env(safe-area-inset-bottom), 16px) + 76px)',
        background: 'linear-gradient(180deg, transparent, rgba(245,197,24,0.02))',
      }}
    >
      <div
        style={{
          maxWidth: 'var(--shell-max)',
          margin: '0 auto',
          padding: '0 16px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 24,
          fontSize: 12.5,
          color: 'var(--color-fog)',
          lineHeight: 1.6,
        }}
      >
        <div>
          <div className="h-serif" style={{ fontSize: 18, color: 'var(--color-bone)' }}>
            Coins<em style={{ fontStyle: 'normal', color: 'var(--color-acid)' }}>Rubot</em>
          </div>
          <p style={{ marginTop: 8, marginBottom: 0, fontSize: 11.5, opacity: 0.8 }}>
            Пополнение TikTok Coins, Telegram Stars и Steam. Оплата по СБП и картой.
          </p>
        </div>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--color-ash)', marginBottom: 8, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Сервис
          </div>
          <Link href="/reviews" style={{ display: 'block', color: 'var(--color-fog)', textDecoration: 'none', marginBottom: 6 }}>
            Отзывы
          </Link>
          <Link href="/privacy" style={{ display: 'block', color: 'var(--color-fog)', textDecoration: 'none', marginBottom: 6 }}>
            Политика конфиденциальности
          </Link>
          <Link href="/agreement" style={{ display: 'block', color: 'var(--color-fog)', textDecoration: 'none' }}>
            Пользовательское соглашение
          </Link>
        </div>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--color-ash)', marginBottom: 8, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Поддержка
          </div>
          <a href="https://t.me/Ahmadaaww" target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--color-fog)', textDecoration: 'none', marginBottom: 6 }}>
            @Ahmadaaww — Telegram
          </a>
          <span style={{ display: 'block', opacity: 0.8 }}>10:00–02:00 МСК · чат по заказу — после оплаты</span>
        </div>
      </div>
      <p
        style={{
          maxWidth: 'var(--shell-max)',
          margin: '22px auto 0',
          padding: '0 16px',
          fontSize: 10.5,
          opacity: 0.6,
        }}
      >
        Сервис не аффилирован с TikTok, Telegram и Steam. Все товарные знаки принадлежат их владельцам. · Обновление от 19.09.2026
      </p>
    </footer>
  )
}

function TopNav() {
  const [location] = useLocation()
  const { data: me } = trpc.auth.me.useQuery()
  const isAdmin = me?.role === 'admin' || isAdminIdentifier(me?.telegramId, me?.telegramUsername, me?.email)
  const display = me?.telegramUsername ? `@${me.telegramUsername}` : me?.email

  return (
    <div className="nav-bar">
      <div className="nav-inner">
        <Link href="/" className="nav-brand">
          Coins<em>·</em>Rubot
        </Link>
        <nav className="nav-links" aria-label="Навигация">
          <a href="/#catalog" className={`nav-link${location === '/' ? ' nav-link-active' : ''}`}>
            Каталог
          </a>
          <Link href="/reviews" className={`nav-link${location.startsWith('/reviews') ? ' nav-link-active' : ''}`}>
            Отзывы
          </Link>
          <a href="https://t.me/Ahmadaaww" target="_blank" rel="noopener noreferrer" className="nav-link">
            Поддержка
          </a>
          {isAdmin && (
            <Link href="/admin" className={`nav-link${location.startsWith('/admin') ? ' nav-link-active' : ''}`}>
              Админ-панель
            </Link>
          )}
        </nav>
        <span className="nav-spacer" />
        {display ? (
          <Link
            href="/profile"
            className="pill"
            style={{
              textDecoration: 'none', fontSize: 12, fontWeight: 700,
              padding: '7px 13px', color: 'var(--color-bone)',
              border: '1px solid var(--color-line-2)', whiteSpace: 'nowrap',
            }}
            title="Профиль"
          >
            <User size={13} strokeWidth={2.4} style={{ verticalAlign: -2, marginRight: 5 }} />
            {display.length > 16 ? display.slice(0, 15) + '…' : display}
          </Link>
        ) : (
          <Link href="/login" className="btn btn-ghost" style={{ padding: '9px 16px', fontSize: 13 }}>
            Войти
          </Link>
        )}
      </div>
    </div>
  )
}

function Brand() {
  return (
    <div className="brandmark" style={{ fontSize: 18 }}>
      <span className="coin" aria-hidden />
      <span>CoinsRubot</span>
    </div>
  )
}

function BottomNav() {
  const [location] = useLocation()
  const { data: me } = trpc.auth.me.useQuery()
  const isAdmin = me?.role === 'admin' || isAdminIdentifier(me?.telegramId, me?.telegramUsername, me?.email)

  const tabs = [
    { href: '/', label: 'Магазин', icon: HomeIcon },
    { href: '/profile', label: 'Профиль', icon: User },
  ]

  // Only show admin tab to admins
  if (isAdmin) {
    tabs.push({ href: '/admin', label: 'Админ', icon: ShieldCheck })
  }

  return (
    <nav className="bottom-nav" aria-label="Навигация">
      {tabs.map(({ href, label, icon: Icon }) => {
        const active = href === '/' ? location === '/' : location.startsWith(href)
        return (
          <Link key={href} href={href} className={active ? 'active' : ''}>
            <Icon size={18} strokeWidth={active ? 2.6 : 2} />
            <span>{label}</span>
          </Link>
        )
      })}
    </nav>
  )
}

function PageLoader() {
  return (
    <div style={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
      <div
        style={{
          width: 34, height: 34, borderRadius: '50%',
          border: '3px solid var(--color-line-2)', borderTopColor: 'var(--color-acid)',
          animation: 'spin 0.8s linear infinite',
        }}
      />
    </div>
  )
}

/** The shop's routes — shared by the classic shell and design v2. */
function ShopRoutes({
  home,
  product,
  games,
}: {
  home: React.ComponentType
  product?: React.ComponentType<{ id: string }>
  games?: React.ComponentType
}) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={home} />
        <Route path="/login" component={Login} />
        <Route path="/admin" component={Admin} />
        <Route path="/reviews" component={Reviews} />
        <Route path="/games" component={games ?? Games} />
        {product && (
          <Route path="/p/:id">{params => {
            const P = product as React.ComponentType<{ id: string }>
            return <P id={(params as { id: string }).id} />
          }}</Route>
        )}
        <Route path="/privacy" component={PrivacyPolicy} />
        <Route path="/agreement" component={UserAgreement} />
        <Route path="/payment-success" component={PaymentSuccess} />
        <Route path="/payment-fail" component={PaymentFail} />
        <Route path="/pay/:orderRef" component={PaymentBridge} />
        <Route path="/chat/:chatId" component={Chat} />
        <Route path="/support/:chatId" component={SupportChat} />
        <Route path="/profile" component={Profile} />
        <Route>
          <div style={{ textAlign: 'center', padding: '20vh 0', color: 'var(--color-ash)' }}>
            <h2 className="h-serif" style={{ fontSize: 34 }}>404</h2>
            <p style={{ marginTop: 8 }}>Страница не найдена</p>
          </div>
        </Route>
      </Switch>
    </Suspense>
  )
}

function App() {
  // Auto sign-in inside the Telegram mini-app: validate initData → session cookie.
  const { initData, isTelegramMiniApp } = useTelegram()
  const utils = trpc.useUtils()
  const tgLogin = trpc.auth.telegramMiniApp.useMutation()
  const attemptedRef = useRef(false)

  useEffect(() => {
    if (attemptedRef.current) return
    if (!initData || initData.length === 0) return

    attemptedRef.current = true
    tgLogin.mutate(
      { initData },
      { onSuccess: () => utils.auth.me.invalidate(), onError: () => {} }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData])

  // ── Ad-campaign promo capture ── landing links carry ?promo=CODE (Yandex/
  // VK first-order campaigns). Stashed for the session; checkout auto-applies.
  useEffect(() => {
    stashPromoFromUrl()
  }, [])

  // ── Visitor analytics beacon ── once per browser session. visitorId lives in
  // localStorage (identifies returning visitors); sessionId in sessionStorage
  // (one beacon per session). Non-JS bots never fire this. Server-side UA
  // filter flags headless ones.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('coinsrubot_tracked')) return
      sessionStorage.setItem('coinsrubot_tracked', '1')

      let visitorId = localStorage.getItem('coinsrubot_vid')
      if (!visitorId) {
        visitorId = crypto.randomUUID()
        localStorage.setItem('coinsrubot_vid', visitorId)
      }
      const sessionId = crypto.randomUUID()

      const params = new URLSearchParams(window.location.search)
      fetch('/api/track/visit', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          visitorId,
          sessionId,
          referrer: document.referrer || undefined,
          utmSource: params.get('utm_source') || undefined,
          utmCampaign: params.get('utm_campaign') || undefined,
          utmMedium: params.get('utm_medium') || undefined,
          landingPath: window.location.pathname,
          isTelegram: isTelegramMiniApp,
          webdriver: (navigator as any).webdriver === true,
        }),
      }).catch(() => {})
    } catch { /* analytics must never break the app */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // DEV ONLY: auto-login as admin when NOT in the Telegram Mini App (so you can
  // test locally in a browser without Telegram). Blocked in production builds.
  const devLogin = trpc.auth.devLogin.useMutation()
  const devAttemptedRef = useRef(false)
  useEffect(() => {
    if (!(import.meta as any).env?.DEV) return
    if (devAttemptedRef.current) return
    if (initData && initData.length > 0) return // Mini App → use real login
    // Customer-mode flag: screenshots/QA run as a clean customer (no admin).
    if (sessionStorage.getItem('coinsrubot_customer')) return
    devAttemptedRef.current = true
    devLogin.mutate(undefined, { onSuccess: () => utils.auth.me.invalidate(), onError: () => {} })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData])

  // Chat routes render FULL-SCREEN outside the shop shell (no TopNav/Footer/
  // BottomNav). The chat screen is exactly the viewport tall with a single
  // internal scroller — nesting it inside the scrolling shell is what caused
  // the page to jump around and hide the composer.
  const [location] = useLocation()

  // Yandex Metrika: capture ad tags once, then one hit per route change (the
  // first hit included — the tag runs with ssr:true and sends none itself).
  useEffect(() => {
    captureTrafficSource()
    trackHit()
  }, [location])

  // IMPORTANT: every hook must run on EVERY render of App — including chat
  // routes. This used to sit below the chat early-return, so navigating from
  // a shop page to /chat or /support changed the hook count and React threw
  // #300/#310 ("Rendered fewer/more hooks than expected") — white screen on
  // the first visit and a normal page after a reload (which mounts directly
  // on the chat route). Keep it above the early return.
  const { v2 } = useUiMode()

  if (location.startsWith('/chat/') || location.startsWith('/support/')) {
    return (
      <>
        <div className="bazaar-atmosphere" aria-hidden />
        <Suspense fallback={<PageLoader />}>
          <ChatErrorBoundary>
          <Switch>
            <Route path="/chat/:chatId" component={Chat} />
            <Route path="/support/:chatId" component={SupportChat} />
            <Route>
              <div style={{ height: '100dvh', display: 'grid', placeItems: 'center', color: 'var(--color-ash)' }}>
                <div style={{ textAlign: 'center' }}>
                  <h2 className="h-serif" style={{ fontSize: 30, marginBottom: 6 }}>404</h2>
                  <p>Чат не найден</p>
                </div>
              </div>
            </Route>
          </Switch>
          </ChatErrorBoundary>
        </Suspense>
      </>
    )
  }

  // `v2` is computed above (before the chat early-return) to keep the hook
  // order stable across routes.
  if (v2) {
    return (
      <div className="v2-shell">
        <div className="bazaar-atmosphere" aria-hidden />
        <HeaderV2 />
        <main className="v2-main">
          <ShopRoutes home={HomeV2} product={ProductV2} games={GamesV2} />
        </main>
        <FooterV2 />
        <BottomNavV2 />
      </div>
    )
  }

  return (
    <div className="app-frame">
      <div className="bazaar-atmosphere" aria-hidden />
      <div className="shell">
        <TopNav />
        <main className="shell-main">
          <ShopRoutes home={Home} />
        </main>

        <Footer />
      </div>

      <BottomNav />
    </div>
  )
}

export default App
